// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

export interface VideoProps {
    src: string;
    autoPlay?: boolean;
}

type Props = VideoProps;

export interface VideoStyles {
    wrapper: InjectableCss;
}

export const videoStyles: VideoStyles = {
    wrapper: {
        css: css`
            iframe {
                border-width: 0;
            }
        `,
    },
};

const styles = videoStyles;

const Video: React.FC<Props> = ({ src, autoPlay }) => {
    const [srcUrl, setSrcUrl] = React.useState<string>("");

    React.useEffect(() => {
        const videoSrc = getNormalizedVideoSrc(src);
        setSrcUrl(videoSrc);
    }, [src]);

    const applyAutoPlay = (src: string) => {
        if (!autoPlay) {
            return src;
        }
        const autoPlayString = "autoplay=1";
        const hasQueryString = src && src.includes("?");
        const operand = hasQueryString ? "&" : "?";

        return src.concat(`${operand}${autoPlayString}`);
    };

    const extractVideoId = (src: string) => {
        if (!src) {
            return;
        }

        const embedRegex = /(\embed\/)+([^\/]+)/;
        const normalVideoRegex = /(\?|&)v=([^&#]+)/;

        const normalVideoParts = src.match(normalVideoRegex);
        const embedVideoParts = src.match(embedRegex);

        const parts = normalVideoParts || embedVideoParts;

        if (!parts) {
            // the video id is already provided
            return src;
        }

        const idPart = parts[2] || "";

        if (idPart.includes("?")) {
            return idPart.substring(0, idPart.indexOf("?"));
        }

        return idPart;
    };

    const getNormalizedVideoSrc = (src: string) => {
        const videoId = extractVideoId(src);
        const videoSrc = `https://www.youtube.com/embed/${videoId}?feature=oembed&enablejsapi=1`;
        return applyAutoPlay(videoSrc);
    };

    if (!srcUrl) {
        return null;
    }
    
    const videoId = extractVideoId(src);

    return (
        <StyledWrapper {...styles.wrapper}>
            <iframe width="100%" height="100%" src={srcUrl} allow="autoplay;" allowFullScreen={true} />
            <div itemScope itemType="https://schema.org/VideoObject">
                <div itemID="https://www.youtube.com" itemType="https://schema.org/WebSite">
                    <meta itemProp="url" content={`https://www.youtube.com/embed/${videoId}`} />
                    <meta itemProp="contentUrl" content={`https://www.youtube.com/embed/${videoId}`} />
                    <meta itemProp="thumbnailUrl" content={`http://img.youtube.com/vi/${videoId}/maxresdefault.jpg`} />
                </div>
            </div>
        </StyledWrapper>
    );
};

export default Video;
